import {
  DefaultApi,
  type AddWalletDto,
  type CompleteWalletVerificationDto,
  type CreatePostRequestAttachmentDto,
  type CreatePostRequestDto,
  type JoinCampaignRequestDto,
  type StartWalletVerificationDto,
  type TwoFactorVerificationRequest,
  type UpdateUserBasicSettingsDto,
  type UpdateUserEmailDto,
  type UpdateUserTwoFactorEnabledDto,
  type WithdrawRequestDto,
} from "../../_api_generated_/";

type GetTwitterLoginUrlParams = {
  deviceId?: string;
  trigger?: string;
  domainType?: string;
  partnerName?: string;
  campaignId?: string;
  campaignIndex?: number;
  referrer?: string;
  page_path?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  invite_id?: string;
};

type GetPostPermissionsRequestedUrlParams = {
  trigger?: string;
  page_path?: string;
  user_id?: string;
  modalToOpen?: string;
};

export default {
  getApiClient() {
    return new DefaultApi(undefined, undefined, useAxios());
  },

  getTwitterLoginUrl(params: GetTwitterLoginUrlParams) {
    const currentOrigin = window.location.origin;
    const callbackUrl = currentOrigin + "/";

    return this.getApiClient().authControllerTwitterReadLogin(
      callbackUrl,
      callbackUrl + "?login=cancelled",
      params.deviceId,
      params.trigger,
      params.domainType,
      params.partnerName,
      params.campaignId,
      params.campaignIndex,
      params.referrer,
      params.page_path,
      params.utm_source,
      params.utm_medium,
      params.utm_campaign,
      params.utm_term,
      params.utm_content,
      params.invite_id,
    );
  },

  getTwitterAuthorLoginUrl(params: GetPostPermissionsRequestedUrlParams) {
    const callbackUrl = `${window.location.origin}${params.page_path}`;
    const cancelUrl = `${window.location.origin}?login=cancelled`;
    return this.getApiClient().authControllerTwitterWriteLogin(
      callbackUrl,
      cancelUrl,
      params.page_path,
      params.trigger,
      params.modalToOpen,
    );
  },

  verifyTransaction(
    withdrawalId: string,
    payload: TwoFactorVerificationRequest,
  ) {
    return this.getApiClient().withdrawalControllerVerifyWithdrawal(
      withdrawalId,
      "",
      "",
      payload,
    );
  },

  sendTransactionCodeAgain({ withdrawalId }: { withdrawalId: string }) {
    return this.getApiClient().withdrawalControllerVerifyWithdrawalResend(
      withdrawalId,
    );
  },

  getDashboard() {
    return this.getApiClient().dashboardPageControllerGetDashboardPage();
  },

  getProfile() {
    return this.getApiClient().profilePageControllerGetProfilePage();
  },

  joinCampaign(campaignId: string, data: JoinCampaignRequestDto) {
    return this.getApiClient().joinCampaignControllerJoinCampaign(
      campaignId,
      data,
    );
  },

  submitTweet(
    content = "",
    attachments: CreatePostRequestAttachmentDto[] = [],
    campaignId = "",
    selectedSuggestionId?: string,
  ) {
    const postRequest: CreatePostRequestDto = {
      content: content,
      attachments: attachments,
      campaignId: campaignId,
      selectedSuggestionId: selectedSuggestionId,
    };
    return this.getApiClient().actionControllerCreatePost(postRequest, {
      timeout: 5 * 60 * 1000, // 5 minutes - just a value way longer than the default 15 seconds, no extra reason for why 5
    });
  },

  startWalletVerification(payload: StartWalletVerificationDto) {
    return this.getApiClient().walletControllerStartWalletVerification(payload);
  },
  completeWalletVerification(payload: CompleteWalletVerificationDto) {
    return this.getApiClient().walletControllerCompleteWalletVerification(
      payload,
    );
  },

  getInvitationDetails(publicToken: string) {
    return this.getApiClient().invitationPageControllerGetInvitationPage(
      publicToken,
    );
  },

  makeWithdraw(payload: WithdrawRequestDto) {
    return this.getApiClient().withdrawalControllerRequestWithdraw(
      "",
      "",
      payload,
    );
  },

  refreshNotifications() {
    return this.getApiClient().notificationControllerGetNotifications();
  },

  markAsSeen(notificationId: string) {
    return this.getApiClient().notificationControllerMarkNotificationRead(
      notificationId,
    );
  },

  markAllAsSeen() {
    return this.getApiClient().notificationControllerMarkAllNotificationsRead();
  },

  saveBasicSettings(payload: UpdateUserBasicSettingsDto) {
    return this.getApiClient().userSettingsBasicControllerUpdateBasicSettings(
      payload,
    );
  },

  claimAllRewards(campaignId: string) {
    return this.getApiClient().actionControllerConfirmPayout(campaignId);
  },

  setTwoFactor(payload: UpdateUserTwoFactorEnabledDto) {
    return this.getApiClient().userSettingsTwoFactorControllerRequestUserTwoFactorEnabledChange(
      payload,
    );
  },

  verifyTwoFactor(otp: TwoFactorVerificationRequest) {
    return this.getApiClient().userSettingsTwoFactorControllerApproveUserTwoFactorEnabledChange(
      otp,
    );
  },

  setUserEmail(payload: UpdateUserEmailDto) {
    return this.getApiClient().userSettingsEmailControllerRequestUserEmailChange(
      payload,
    );
  },

  verifyUserEmail(otp: TwoFactorVerificationRequest) {
    return this.getApiClient().userSettingsEmailControllerApproveUserEmailChange(
      otp,
    );
  },
};
